import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from 'app/shared/apis/api.service';
import { SocketService } from 'app/services/socket.service';
import { Subject, filter, take, takeUntil, timer } from 'rxjs';
import { DriverAssignmentEvent } from '../types/driver-trip.types';
import { DriverRefreshService } from './driver-refresh.service';
import { DriverTripService } from './driver-trip.service';

@Injectable({ providedIn: 'root' })
export class DriverSocketService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private subscribedUserId: number | null = null;
    private pendingPollStarted = false;

    constructor(
        private readonly socketService: SocketService,
        private readonly driverTripService: DriverTripService,
        private readonly driverRefreshService: DriverRefreshService,
        private readonly router: Router,
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.unsubscribe();
    }

    start(): void {
        ApiService.userCurrent
            .pipe(
                filter((user) => !!user?.user_id),
                take(1),
            )
            .subscribe((user) => {
                this.subscribe(user.user_id);
                this.listenAssignments();
                this.checkPendingOnBoot();
            });
    }

    private subscribe(userId: number): void {
        if (this.subscribedUserId === userId) {
            return;
        }
        this.unsubscribe();
        this.subscribedUserId = userId;
        this.socketService.emit('subscribe_driver', { user_id: userId });
    }

    private unsubscribe(): void {
        if (!this.subscribedUserId) {
            return;
        }
        this.socketService.emit('unsubscribe_driver', { user_id: this.subscribedUserId });
        this.subscribedUserId = null;
    }

    private listenAssignments(): void {
        this.socketService
            .fromEvent<DriverAssignmentEvent>('driver_assignment')
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => this.handleAssignment(event));
    }

    private handleAssignment(event: DriverAssignmentEvent): void {
        if (!event?.service_id) {
            return;
        }

        const serviceId = event.service_id;
        const url = this.router.url.split('?')[0];

        if (event.type === 'assigned') {
            this.driverRefreshService.refreshAll();
            if (!url.includes(`/driver/service/`)) {
                void this.router.navigate(['/driver/service/new', serviceId]);
            }
            return;
        }

        if (event.type === 'started') {
            this.driverRefreshService.refreshAll();
            void this.router.navigate(['/driver/service/route', serviceId]);
            return;
        }

        if (event.type === 'cancelled') {
            this.driverRefreshService.refreshAll();
            if (url.includes(`/driver/service/new/${serviceId}`) || url.includes(`/driver/service/route/${serviceId}`)) {
                void this.router.navigate(['/driver/home']);
            }
            return;
        }

        this.driverRefreshService.refreshAll();
        this.driverTripService.notifyTripRefresh(serviceId);

        if (!url.includes(`/driver/service/`)) {
            this.driverTripService
                .getPendingAssignment()
                .pipe(take(1))
                .subscribe((pending) => {
                    if (pending?.service_id) {
                        void this.router.navigate(['/driver/service/new', pending.service_id]);
                    }
                });
        }
    }

    private checkPendingOnBoot(): void {
        this.pollPendingAssignment();
        if (this.pendingPollStarted) {
            return;
        }
        this.pendingPollStarted = true;
        // Revalida periodicamente: alocação no cockpit pode ocorrer com o app já aberto.
        timer(15_000, 15_000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.pollPendingAssignment());
    }

    private pollPendingAssignment(): void {
        const url = this.router.url.split('?')[0];
        if (url.includes('/driver/service/')) {
            return;
        }

        this.driverTripService
            .getPendingAssignment()
            .pipe(take(1))
            .subscribe((pending) => {
                if (pending?.service_id) {
                    this.driverRefreshService.refreshAll();
                    void this.router.navigate(['/driver/service/new', pending.service_id]);
                }
            });
    }
}
